/**
 * Coordinate transformation utilities for pitch visualizations
 */

import { Point, RotationAngle, PitchDimensions } from './types';
import type { MatchPeriods } from '../../hooks/useMatchPeriods';

// Re-export types for convenience
export type { RotationAngle, PitchDimensions, Point };

// Standard pitch dimensions
export const PITCH_WIDTH = 1050;
export const PITCH_HEIGHT = 680;

/**
 * Rotate a point around the center of the pitch
 */
export function rotatePoint(
  point: Point,
  angle: RotationAngle,
  dimensions: PitchDimensions = { width: PITCH_WIDTH, height: PITCH_HEIGHT }
): Point {
  const centerX = dimensions.width / 2;
  const centerY = dimensions.height / 2;

  // Translate to origin
  const x = point.x - centerX;
  const y = point.y - centerY;

  let rotatedX: number;
  let rotatedY: number;

  switch (angle) {
    case 90:
      // 90° clockwise: (x, y) -> (y, -x)
      rotatedX = y;
      rotatedY = -x;
      break;
    case 180:
      // 180°: (x, y) -> (-x, -y)
      rotatedX = -x;
      rotatedY = -y;
      break;
    case 270:
      // 270° clockwise (90° counter-clockwise): (x, y) -> (-y, x)
      rotatedX = -y;
      rotatedY = x;
      break;
    case 0:
    default:
      rotatedX = x;
      rotatedY = y;
      break;
  }

  // Translate back
  return {
    x: rotatedX + centerX,
    y: rotatedY + centerY,
  };
}

/**
 * Rotate an array of points
 */
export function rotatePoints(
  points: Point[],
  angle: RotationAngle,
  dimensions?: PitchDimensions
): Point[] {
  return points.map((point) => rotatePoint(point, angle, dimensions));
}

/**
 * Scale a point by a factor
 */
export function scalePoint(point: Point, scale: number): Point {
  return {
    x: point.x * scale,
    y: point.y * scale,
  };
}

/**
 * Scale an array of points
 */
export function scalePoints(points: Point[], scale: number): Point[] {
  return points.map((point) => scalePoint(point, scale));
}

/**
 * Transform point: apply rotation then scaling
 */
export function transformPoint(
  point: Point,
  rotation: RotationAngle = 0,
  scale: number = 1,
  dimensions?: PitchDimensions
): Point {
  let transformed = point;
  if (rotation !== 0) {
    transformed = rotatePoint(transformed, rotation, dimensions);
  }
  if (scale !== 1) {
    transformed = scalePoint(transformed, scale);
  }
  return transformed;
}

/**
 * Transform array of points
 */
export function transformPoints(
  points: Point[],
  rotation: RotationAngle = 0,
  scale: number = 1,
  dimensions?: PitchDimensions
): Point[] {
  return points.map((point) => transformPoint(point, rotation, scale, dimensions));
}

/**
 * Get SVG transform string for rotation and scaling
 */
export function getSVGTransform(
  rotation: RotationAngle = 0,
  scale: number = 1,
  dimensions: PitchDimensions = { width: PITCH_WIDTH, height: PITCH_HEIGHT }
): string {
  const centerX = dimensions.width / 2;
  const centerY = dimensions.height / 2;

  const transforms: string[] = [];

  if (rotation !== 0) {
    transforms.push(`rotate(${rotation} ${centerX} ${centerY})`);
  }

  if (scale !== 1) {
    transforms.push(`scale(${scale})`);
  }

  return transforms.join(' ');
}

/**
 * Normalize coordinates from database to viewport
 * Ensures coordinates are within pitch bounds
 */
export function normalizeCoordinates(point: Point): Point {
  return {
    x: Math.max(0, Math.min(PITCH_WIDTH, point.x)),
    y: Math.max(0, Math.min(PITCH_HEIGHT, point.y)),
  };
}

/**
 * Check if a point is within pitch bounds
 */
export function isPointInBounds(
  point: Point,
  dimensions: PitchDimensions = { width: PITCH_WIDTH, height: PITCH_HEIGHT }
): boolean {
  return (
    point.x >= 0 &&
    point.x <= dimensions.width &&
    point.y >= 0 &&
    point.y <= dimensions.height
  );
}

/**
 * Flip a point to normalize second half coordinates to first half perspective
 * This mirrors the pitch horizontally and vertically
 */
export function flipPointToFirstHalf(point: Point): Point {
  return {
    x: PITCH_WIDTH - point.x,
    y: PITCH_HEIGHT - point.y,
  };
}

/**
 * Flip an array of points to normalize second half coordinates
 */
export function flipPointsToFirstHalf(points: Point[]): Point[] {
  return points.map((point) => flipPointToFirstHalf(point));
}

/**
 * Determine which period an event belongs to based on its timestamp
 * Uses inclusive boundaries and handles edge cases
 */
export function getEventPeriod(
  eventTimestamp: string,
  matchPeriods: MatchPeriods | null
): 'first_half' | 'second_half' | 'extra_time_1st_half' | 'extra_time_2nd_half' | 'penalty_shootout' | 'unknown' {
  if (!matchPeriods) return 'unknown';
  
  const timestamp = new Date(eventTimestamp).getTime();
  if (isNaN(timestamp)) {
    console.warn(`[getEventPeriod] Invalid timestamp: ${eventTimestamp}`);
    return 'unknown';
  }
  
  // Check first half (with inclusive boundaries)
  if (matchPeriods.firstHalf?.startTimestamp && matchPeriods.firstHalf?.endTimestamp) {
    const start = new Date(matchPeriods.firstHalf.startTimestamp).getTime();
    const end = new Date(matchPeriods.firstHalf.endTimestamp).getTime();
    if (!isNaN(start) && !isNaN(end) && timestamp >= start && timestamp <= end) {
      return 'first_half';
    }
  }
  
  // Check second half (with inclusive boundaries)
  if (matchPeriods.secondHalf?.startTimestamp && matchPeriods.secondHalf?.endTimestamp) {
    const start = new Date(matchPeriods.secondHalf.startTimestamp).getTime();
    const end = new Date(matchPeriods.secondHalf.endTimestamp).getTime();
    if (!isNaN(start) && !isNaN(end)) {
      if (timestamp >= start && timestamp <= end) {
        return 'second_half';
      }
    }
  }
  
  // Check extra time 1st half
  if (matchPeriods.extraTimeFirstHalf?.startTimestamp && matchPeriods.extraTimeFirstHalf?.endTimestamp) {
    const start = new Date(matchPeriods.extraTimeFirstHalf.startTimestamp).getTime();
    const end = new Date(matchPeriods.extraTimeFirstHalf.endTimestamp).getTime();
    if (!isNaN(start) && !isNaN(end) && timestamp >= start && timestamp <= end) {
      return 'extra_time_1st_half';
    }
  }
  
  // Check extra time 2nd half
  if (matchPeriods.extraTimeSecondHalf?.startTimestamp && matchPeriods.extraTimeSecondHalf?.endTimestamp) {
    const start = new Date(matchPeriods.extraTimeSecondHalf.startTimestamp).getTime();
    const end = new Date(matchPeriods.extraTimeSecondHalf.endTimestamp).getTime();
    if (!isNaN(start) && !isNaN(end) && timestamp >= start && timestamp <= end) {
      return 'extra_time_2nd_half';
    }
  }
  
  // Check penalty shootout
  if (matchPeriods.penaltyShootout?.startTimestamp && matchPeriods.penaltyShootout?.endTimestamp) {
    const start = new Date(matchPeriods.penaltyShootout.startTimestamp).getTime();
    const end = new Date(matchPeriods.penaltyShootout.endTimestamp).getTime();
    if (!isNaN(start) && !isNaN(end) && timestamp >= start && timestamp <= end) {
      return 'penalty_shootout';
    }
  }
  
  // Fallback: If we have second half period defined and event is after first half ends,
  // assume it's second half (handles edge cases where timestamps might be slightly off)
  if (matchPeriods.firstHalf?.endTimestamp && matchPeriods.secondHalf?.startTimestamp) {
    const firstHalfEnd = new Date(matchPeriods.firstHalf.endTimestamp).getTime();
    const secondHalfStart = new Date(matchPeriods.secondHalf.startTimestamp).getTime();
    if (!isNaN(firstHalfEnd) && !isNaN(secondHalfStart) && timestamp > firstHalfEnd) {
      // Check if it's before extra time starts (if extra time exists)
      if (matchPeriods.extraTimeFirstHalf?.startTimestamp) {
        const extraTimeStart = new Date(matchPeriods.extraTimeFirstHalf.startTimestamp).getTime();
        if (!isNaN(extraTimeStart) && timestamp < extraTimeStart) {
          return 'second_half';
        }
      } else if (matchPeriods.secondHalf?.endTimestamp) {
        const secondHalfEnd = new Date(matchPeriods.secondHalf.endTimestamp).getTime();
        if (!isNaN(secondHalfEnd) && timestamp <= secondHalfEnd) {
          return 'second_half';
        }
      } else {
        // No end timestamp for second half, but we're after first half ends
        return 'second_half';
      }
    }
  }
  
  // Fallback: If we have extra time 2nd half period defined and event is after extra time 1st half ends,
  // assume it's extra time 2nd half
  if (matchPeriods.extraTimeFirstHalf?.endTimestamp && matchPeriods.extraTimeSecondHalf?.startTimestamp) {
    const extraTime1stHalfEnd = new Date(matchPeriods.extraTimeFirstHalf.endTimestamp).getTime();
    const extraTime2ndHalfStart = new Date(matchPeriods.extraTimeSecondHalf.startTimestamp).getTime();
    if (!isNaN(extraTime1stHalfEnd) && !isNaN(extraTime2ndHalfStart) && timestamp > extraTime1stHalfEnd) {
      if (matchPeriods.extraTimeSecondHalf?.endTimestamp) {
        const extraTime2ndHalfEnd = new Date(matchPeriods.extraTimeSecondHalf.endTimestamp).getTime();
        if (!isNaN(extraTime2ndHalfEnd) && timestamp <= extraTime2ndHalfEnd) {
          return 'extra_time_2nd_half';
        }
      } else {
        return 'extra_time_2nd_half';
      }
    }
  }
  
  return 'unknown';
}
